/**
 * Groups a YEAR view's bookings, absences and bank holidays into one summary
 * per scheduler row per calendar month, so the timeline can draw a single
 * tile per month instead of one bar per booking.
 */

export const YEAR_SUMMARY_EVENT_ID_PREFIX = 'year-summary-';

export interface YearSummaryBookingInput {
  /** Id of the timeline event the booking is drawn as. */
  eventId: string;
  bookingId: string;
  rowId: string;
  /** Resource the booking tile stands for (differs from rowId under grouping). */
  resourceId: string;
  title: string;
  start: Date;
  end: Date;
  color: string;
  status?: string;
}

export interface YearSummaryAbsenceInput {
  id: string;
  rowId: string;
  label: string;
  reason?: string;
  start: Date;
  end: Date;
  color: string;
  /** Public/bank-holiday absences are listed with the bank holidays. */
  isHoliday: boolean;
}

export interface YearSummaryHolidayInput {
  id: string;
  rowId: string;
  name: string;
  start: Date;
  end: Date;
  color: string;
}

export interface YearSummaryBookingItem extends Omit<YearSummaryBookingInput, 'rowId'> {
  /** Last instant the booking covers, for display. */
  displayEnd: Date;
}

export interface YearSummaryAbsenceItem {
  id: string;
  label: string;
  reason?: string;
  start: Date;
  displayEnd: Date;
  days: number;
  color: string;
}

export interface YearSummaryHolidayItem {
  id: string;
  name: string;
  date: Date;
  color: string;
}

export interface YearSummaryColorSegment {
  color: string;
  count: number;
}

export interface YearMonthSummary {
  id: string;
  rowId: string;
  monthStart: Date;
  /** Exclusive end of the month, clipped to the visible range. */
  monthEnd: Date;
  bookings: YearSummaryBookingItem[];
  absences: YearSummaryAbsenceItem[];
  holidays: YearSummaryHolidayItem[];
  colorSegments: YearSummaryColorSegment[];
  /** Changes whenever anything the tile or its list shows changes. */
  signature: string;
}

export interface BuildYearMonthSummariesInput {
  rangeStart: Date;
  rangeEnd: Date;
  bookings: YearSummaryBookingInput[];
  absences: YearSummaryAbsenceInput[];
  holidays: YearSummaryHolidayInput[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

function displayEndOf(start: Date, end: Date): Date {
  const isMidnight = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0 && end.getMilliseconds() === 0;
  return isMidnight && end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : end;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function calendarDaysBetween(start: Date, endInclusive: Date): number {
  return Math.round((startOfDay(endInclusive).getTime() - startOfDay(start).getTime()) / DAY_MS) + 1;
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Month buckets covering [rangeStart, rangeEnd), each clipped to the range. */
export function monthBuckets(rangeStart: Date, rangeEnd: Date): Array<{ start: Date; end: Date; key: string }> {
  const buckets: Array<{ start: Date; end: Date; key: string }> = [];
  let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
  while (cursor.getTime() < rangeEnd.getTime()) {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const start = cursor.getTime() < rangeStart.getTime() ? rangeStart : cursor;
    const end = next.getTime() > rangeEnd.getTime() ? rangeEnd : next;
    buckets.push({ start, end, key: monthKey(cursor) });
    cursor = next;
  }
  return buckets;
}

export function buildYearMonthSummaries(input: BuildYearMonthSummariesInput): YearMonthSummary[] {
  const { rangeStart, rangeEnd } = input;
  const buckets = monthBuckets(rangeStart, rangeEnd);
  if (buckets.length === 0) return [];

  const summaries = new Map<string, YearMonthSummary>();
  const holidayKeys = new Set<string>();

  const summaryFor = (rowId: string, bucketIndex: number): YearMonthSummary => {
    const bucket = buckets[bucketIndex];
    const id = `${YEAR_SUMMARY_EVENT_ID_PREFIX}${rowId}-${bucket.key}`;
    let summary = summaries.get(id);
    if (!summary) {
      summary = {
        id,
        rowId,
        monthStart: bucket.start,
        monthEnd: bucket.end,
        bookings: [],
        absences: [],
        holidays: [],
        colorSegments: [],
        signature: '',
      };
      summaries.set(id, summary);
    }
    return summary;
  };

  // Indexes of the buckets that [start, end) overlaps.
  const overlappedBuckets = (start: Date, end: Date): number[] => {
    const s = start.getTime();
    const e = Math.max(end.getTime(), s + 1);
    const indexes: number[] = [];
    buckets.forEach((bucket, index) => {
      if (s < bucket.end.getTime() && e > bucket.start.getTime()) indexes.push(index);
    });
    return indexes;
  };

  const addHoliday = (rowId: string, id: string, name: string, date: Date, color: string) => {
    const day = startOfDay(date);
    overlappedBuckets(day, new Date(day.getTime() + DAY_MS)).forEach((bucketIndex) => {
      const dedupeKey = `${rowId}|${day.getTime()}|${name.trim().toLowerCase()}`;
      if (holidayKeys.has(dedupeKey)) return;
      holidayKeys.add(dedupeKey);
      summaryFor(rowId, bucketIndex).holidays.push({ id, name, date: day, color });
    });
  };

  input.bookings.forEach((booking) => {
    const displayEnd = displayEndOf(booking.start, booking.end);
    overlappedBuckets(booking.start, booking.end).forEach((bucketIndex) => {
      summaryFor(booking.rowId, bucketIndex).bookings.push({
        eventId: booking.eventId,
        bookingId: booking.bookingId,
        resourceId: booking.resourceId,
        title: booking.title,
        start: booking.start,
        end: booking.end,
        displayEnd,
        color: booking.color,
        status: booking.status,
      });
    });
  });

  input.absences.forEach((absence) => {
    if (absence.isHoliday) {
      const last = displayEndOf(absence.start, absence.end);
      for (let day = startOfDay(absence.start); day.getTime() <= last.getTime(); day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)) {
        addHoliday(absence.rowId, absence.id, absence.label, day, absence.color);
      }
      return;
    }
    const displayEnd = displayEndOf(absence.start, absence.end);
    overlappedBuckets(absence.start, absence.end).forEach((bucketIndex) => {
      summaryFor(absence.rowId, bucketIndex).absences.push({
        id: absence.id,
        label: absence.label,
        reason: absence.reason,
        start: absence.start,
        displayEnd,
        days: calendarDaysBetween(absence.start, displayEnd),
        color: absence.color,
      });
    });
  });

  input.holidays.forEach((holiday) => {
    addHoliday(holiday.rowId, holiday.id, holiday.name, holiday.start, holiday.color);
  });

  const byStart = (a: { start: Date }, b: { start: Date }) => a.start.getTime() - b.start.getTime();
  summaries.forEach((summary) => {
    summary.bookings.sort(byStart);
    summary.absences.sort(byStart);
    summary.holidays.sort((a, b) => a.date.getTime() - b.date.getTime());

    const counts = new Map<string, number>();
    summary.bookings.forEach((b) => counts.set(b.color, (counts.get(b.color) ?? 0) + 1));
    summary.colorSegments = Array.from(counts, ([color, count]) => ({ color, count }))
      .sort((a, b) => b.count - a.count);

    summary.signature = [
      summary.bookings.map((b) => `${b.eventId}:${b.start.getTime()}:${b.end.getTime()}:${b.color}:${b.status ?? ''}:${b.title}`).join(','),
      summary.absences.map((a) => `${a.id}:${a.start.getTime()}:${a.displayEnd.getTime()}:${a.color}:${a.label}`).join(','),
      summary.holidays.map((h) => `${h.id}:${h.date.getTime()}:${h.color}:${h.name}`).join(','),
    ].join('|');
  });

  return Array.from(summaries.values());
}
